import { useTranslation } from "react-i18next";
import React, { useState, Children } from 'react';
import { ArrowLeftIcon, PlusIcon, TrashIcon, AlertCircleIcon } from 'lucide-react';
interface AddRoomFormProps {
  onSave: (roomData: any) => void;
  onCancel: () => void;
}
export function AddRoomForm({
  onSave,
  onCancel
}: AddRoomFormProps) {
  const {
    t
  } = useTranslation("rooms");
  const [roomData, setRoomData] = useState({
    name: '',
    category: '',
    code: '',
    description: '',
    isSmoking: false,
    isAccessible: false,
    isActive: false,
    view: '',
    size: '',
    amenities: [] as string[],
    baseAdultsOccupancy: 2,
    baseChildrenOccupancy: 0,
    maxOccupancy: 2,
    allowAdditionalAdults: false,
    allowAdditionalChildren: false,
    bedConfigurations: [{
      type: '',
      count: 1
    }]
  });
  const roomCategories = ['SUPERIOR', 'DELUXE', 'OVERWATER_VILLA', 'BEACH_VILLA', 'SUITE'];
  const roomViews = ['SEA_VIEW', 'GARDEN_VIEW', 'OCEAN_VIEW', 'LAGOON_VIEW', 'BEACH_VIEW'];
  const amenityOptions = ['Wi-Fi', 'TV', 'Minibar', 'Air Conditioning', 'Balcony', 'Terrace', 'Ocean View', 'Private Pool', 'Jacuzzi', 'Slippers', 'Desk', 'Dining Area', 'Interconnecting Rooms', 'Non-Smoking', 'Beach Access', 'Breakfast Included'];
  const bedTypes = ['King', 'Queen', 'Double', 'Twin', 'Single', 'Sofa Bed', 'Bunk Bed'];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  const handleNumberChange = (name: string, value: string) => {
    const numValue = value === '' ? 0 : parseInt(value, 10);
    setRoomData(prev => ({
      ...prev,
      [name]: numValue
    }));
  };
  const handleBedConfigChange = (index: number, field: string, value: string) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    if (field === 'count') {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: parseInt(value, 10) || 1
      };
    } else {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: value
      };
    }
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const addBedConfig = () => {
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: [...prev.bedConfigurations, {
        type: '',
        count: 1
      }]
    }));
  };
  const removeBedConfig = (index: number) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    updatedConfigs.splice(index, 1);
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(roomData);
  };
  return <div className="space-y-6" data-id="element-8651">
      <div className="flex items-center" data-id="element-8652">
        <button onClick={onCancel} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-8653">
          
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-8654" />
          <span data-id="element-8655">{t("components.AddRoomForm.add_room")}</span>
        </button>
      </div>
      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-8656">
        
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-8657">
          <div data-id="element-8658">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8659">
              
              {t("components.AddRoomForm.room_name")}<span className="text-red-500" data-id="element-8660">*</span>
            </label>
            <input type="text" id="name" name="name" value={roomData.name} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8661" />
            
          </div>
          <div data-id="element-8662">
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8663">
              
              {t("components.AddRoomForm.room_category")}<span className="text-red-500" data-id="element-8664">*</span>
            </label>
            <div className="relative" data-id="element-8665">
              <select id="category" name="category" value={roomData.category} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-8666">
                
                <option value="" data-id="element-8667">{t("components.AddRoomForm.select_room_category")}</option>
                {roomCategories.map(category => <option key={category} value={category} data-id="element-8668">
                    {category.replace('_', ' ')}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-8669">
                <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-8670">
                  
                  <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-8671" />
                  
                </svg>
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-8672">
          <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8673">
            
            {t("components.AddRoomForm.room_code")}
          </label>
          <input type="text" id="code" name="code" value={roomData.code} onChange={handleInputChange} placeholder={t("components.AddRoomForm.attrs.enter_room_code")} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8674" />
          
        </div>
        <div className="mb-6" data-id="element-8675">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8676">
            
            {t("components.AddRoomForm.description")}
          </label>
          <textarea id="description" name="description" value={roomData.description} onChange={handleInputChange} placeholder={t("components.AddRoomForm.attrs.enter_room_description")} rows={4} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8677" />
          
        </div>
        <div className="flex flex-wrap gap-6 mb-6" data-id="element-8678">
          <div className="flex items-center" data-id="element-8679">
            <input type="checkbox" id="isSmoking" name="isSmoking" checked={roomData.isSmoking} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-8680" />
            
            <label htmlFor="isSmoking" className="ml-2 text-sm text-gray-700" data-id="element-8681">
              {t("components.AddRoomForm.smoking")}
            </label>
          </div>
          <div className="flex items-center" data-id="element-8682">
            <input type="checkbox" id="isAccessible" name="isAccessible" checked={roomData.isAccessible} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-8683" />
            
            <label htmlFor="isAccessible" className="ml-2 text-sm text-gray-700" data-id="element-8684">
              
              {t("components.AddRoomForm.accessibility")}
            </label>
          </div>
          <div className="flex items-center" data-id="element-8685">
            <input type="checkbox" id="isActive" name="isActive" checked={roomData.isActive} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-8686" />
            
            <label htmlFor="isActive" className="ml-2 text-sm text-gray-700" data-id="element-8687">
              {t("components.AddRoomForm.status")}
            </label>
          </div>
        </div>
        {/* Room View and Size */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-8688">
          <div data-id="element-8689">
            <label htmlFor="view" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8690">
              
              {t("components.AddRoomForm.room_view")}<span className="text-red-500" data-id="element-8691">*</span>
            </label>
            <div className="relative" data-id="element-8692">
              <select id="view" name="view" value={roomData.view} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-8693">
                
                <option value="" data-id="element-8694">{t("components.AddRoomForm.select_room_view")}</option>
                {roomViews.map(view => <option key={view} value={view} data-id="element-8695">
                    {view.replace('_', ' ')}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-8696">
                <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-8697">
                  
                  <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-8698" />
                  
                </svg>
              </div>
            </div>
          </div>
          <div data-id="element-8699">
            <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8700">
              
              {t("components.AddRoomForm.room_size_sq_m")}
            </label>
            <input type="number" id="size" name="size" value={roomData.size} onChange={handleInputChange} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8701" />
            
          </div>
        </div>
        {/* Room Amenities */}
        <div className="mb-6" data-id="element-8702">
          <label htmlFor="amenities" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8703">
            
            {t("components.AddRoomForm.room_amenities")}
          </label>
          <select id="amenities" name="amenities" multiple value={roomData.amenities} onChange={e => {
          const options = Array.from(e.target.selectedOptions, option => option.value);
          setRoomData(prev => ({
            ...prev,
            amenities: options
          }));
        }} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8704">
            
            {amenityOptions.map(amenity => <option key={amenity} value={amenity} data-id="element-8705">
                {amenity}
              </option>)}
          </select>
        </div>
        {/* Occupancy Settings */}
        <div className="mb-6" data-id="element-8706">
          <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-8707">
            {t("components.AddRoomForm.occupancy_settings")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4" data-id="element-8708">
            <div data-id="element-8709">
              <label htmlFor="baseAdultsOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8710">
                
                {t("components.AddRoomForm.base_adults_occupancy")}<span className="text-red-500" data-id="element-8711">*</span>
              </label>
              <input type="number" id="baseAdultsOccupancy" value={roomData.baseAdultsOccupancy} onChange={e => handleNumberChange('baseAdultsOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8712" />
              
              <p className="text-xs text-gray-500 mt-1" data-id="element-8713">
                {t("components.AddRoomForm.standard_number_of_allowed_adults_for")}
              </p>
            </div>
            <div data-id="element-8714">
              <label htmlFor="baseChildrenOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8715">
                
                {t("components.AddRoomForm.base_children_occupancy")}<span className="text-red-500" data-id="element-8716">*</span>
              </label>
              <input type="number" id="baseChildrenOccupancy" value={roomData.baseChildrenOccupancy} onChange={e => handleNumberChange('baseChildrenOccupancy', e.target.value)} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8717" />
              
              <p className="text-xs text-gray-500 mt-1" data-id="element-8718">
                {t("components.AddRoomForm.standard_number_of_allowed_children_for")}
              </p>
            </div>
            <div data-id="element-8719">
              <label htmlFor="maxOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8720">
                
                {t("components.AddRoomForm.maximum_occupancy")}<span className="text-red-500" data-id="element-8721">*</span>
              </label>
              <input type="number" id="maxOccupancy" value={roomData.maxOccupancy} onChange={e => handleNumberChange('maxOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8722" />
              
              <p className="text-xs text-gray-500 mt-1" data-id="element-8723">
                {t("components.AddRoomForm.maximum_number_of_guests_allowed")}
              </p>
            </div>
          </div>
          <div className="space-y-2 mb-4" data-id="element-8724">
            <div className="flex items-center" data-id="element-8725">
              <input type="checkbox" id="allowAdditionalAdults" name="allowAdditionalAdults" checked={roomData.allowAdditionalAdults} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-8726" />
              
              <label htmlFor="allowAdditionalAdults" className="ml-2 text-sm text-gray-700" data-id="element-8727">
                
                {t("components.AddRoomForm.allow_additional_adults")}
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-6" data-id="element-8728">
              {t("components.AddRoomForm.allow_additional_adults_not_exceeding_total")}
            </p>
            <div className="flex items-center mt-2" data-id="element-8729">
              <input type="checkbox" id="allowAdditionalChildren" name="allowAdditionalChildren" checked={roomData.allowAdditionalChildren} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-8730" />
              
              <label htmlFor="allowAdditionalChildren" className="ml-2 text-sm text-gray-700" data-id="element-8731">
                
                {t("components.AddRoomForm.allow_additional_children")}
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-6" data-id="element-8732">
              {t("components.AddRoomForm.allow_additional_children_not_exceeding_total")}
            </p>
          </div>
          {/* Occupancy Summary */}
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4" data-id="element-8733">
            <div className="flex" data-id="element-8734">
              <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2" data-id="element-8735" />
              <div data-id="element-8736">
                <p className="text-sm text-red-700" data-id="element-8737">
                  {t("components.AddRoomForm.this_room_has_a_base_occupancy")}{' '}
                  {roomData.baseAdultsOccupancy} {t("components.AddRoomForm.adults_and")}{' '}
                  {roomData.baseChildrenOccupancy} {t("components.AddRoomForm.children_with_a_total_maximum_occupancy")} {roomData.maxOccupancy}.
                </p>
                <p className="text-sm text-red-700" data-id="element-8738">
                  {t("components.AddRoomForm.additional_adults_are_not_allowed")}
                </p>
                <p className="text-sm text-red-700" data-id="element-8739">
                  {t("components.AddRoomForm.additional_children_are_not_allowed")}
                </p>
                <p className="text-sm text-red-700" data-id="element-8740">
                  {t("components.AddRoomForm.additional_cost_per_child_or_adults")}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Bed Configuration */}
        <div className="mb-6" data-id="element-8741">
          <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-8742">
            {t("components.AddRoomForm.bed_configuration")}<span className="text-red-500" data-id="element-8743">*</span>
          </h3>
          {roomData.bedConfigurations.map((config, index) => <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-end" data-id="element-8744">
            
              <div data-id="element-8745">
                <label htmlFor={`bedType-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8746">
                
                  {t("components.AddRoomForm.bed_type")}
                </label>
                <div className="relative" data-id="element-8747">
                  <select id={`bedType-${index}`} value={config.type} onChange={e => handleBedConfigChange(index, 'type', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-8748">
                  
                    <option value="" data-id="element-8749">{t("components.AddRoomForm.select_bed_type")}</option>
                    {bedTypes.map(type => <option key={type} value={type} data-id="element-8750">
                        {type}
                      </option>)}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-8751">
                    <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-8752">
                    
                      <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-8753" />
                    
                    </svg>
                  </div>
                </div>
              </div>
              <div className="flex items-center" data-id="element-8754">
                <div className="flex-grow" data-id="element-8755">
                  <label htmlFor={`bedCount-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8756">
                  
                    {t("components.AddRoomForm.bed_count")}
                  </label>
                  <input type="number" id={`bedCount-${index}`} value={config.count} onChange={e => handleBedConfigChange(index, 'count', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8757" />
                
                </div>
                {index > 0 && <button type="button" onClick={() => removeBedConfig(index)} className="ml-2 mt-6 p-2 text-red-600 hover:text-red-800" data-id="element-8758">
                
                    <TrashIcon size={18} data-id="element-8759" />
                  </button>}
              </div>
            </div>)}
          <button type="button" onClick={addBedConfig} className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-8760">
            
            <PlusIcon size={16} className="mr-1" data-id="element-8761" />
            {t("components.AddRoomForm.add_bed_configuration")}
          </button>
        </div>
        {/* Submit Button */}
        <div className="flex justify-end" data-id="element-8762">
          <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-id="element-8763">
            
            {t("components.AddRoomForm.save")}
          </button>
        </div>
      </form>
    </div>;
}